import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axiosInstance from '../api/axios';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';

const MagicLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setErrorMsg('Kein Token gefunden. Bitte fordern Sie einen neuen Magic-Link an.');
      return;
    }

    (async () => {
      try {
        const response = await axiosInstance.post('/auth/magic-link/verify', { token });
        const { user, token: jwt, claimed_orders_count } = response.data;

        const now = Date.now();
        const authState = {
          version: 1,
          state: { user, token: jwt, isAuthenticated: true, lastActivity: now },
        };
        try {
          localStorage.setItem('auth-storage', JSON.stringify(authState));
          localStorage.setItem('last-activity', now.toString());
          localStorage.setItem('token', jwt);
        } catch (_) {}

        setAuth(user, jwt);
        setStatus('success');
        toast.success('Erfolgreich eingeloggt!');

        if (claimed_orders_count && claimed_orders_count > 0) {
          toast.success(
            claimed_orders_count === 1
              ? '1 Gast-Bestellung wurde Ihrem Konto zugeordnet.'
              : `${claimed_orders_count} Gast-Bestellungen wurden Ihrem Konto zugeordnet.`,
            { duration: 6000 }
          );
        }

        const target = user?.role === 'admin' ? '/admin' : '/';
        setTimeout(() => {
          window.location.href = target;
        }, 1200);
      } catch (err) {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'Magic-Link ungültig oder abgelaufen.');
      }
    })();
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: '#F5F0EB' }}
      data-testid="magic-login-page"
    >
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Magic-Link Login</CardTitle>
          <CardDescription>
            {status === 'verifying' && 'Wir prüfen Ihren Link…'}
            {status === 'success' && 'Sie werden weitergeleitet…'}
            {status === 'error' && 'Es ist ein Problem aufgetreten.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4 py-6">
          {status === 'verifying' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#C4A084' }} />
              <p className="text-sm text-gray-600" data-testid="magic-login-verifying">Bitte einen Moment Geduld…</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle2 className="h-12 w-12" style={{ color: '#16a34a' }} />
              <p className="text-sm text-gray-700" data-testid="magic-login-success">
                Anmeldung erfolgreich! Sie werden gleich weitergeleitet.
              </p>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 text-red-500" />
              <p className="text-sm text-gray-700 text-center" data-testid="magic-login-error">
                {errorMsg}
              </p>
              <div className="flex flex-col gap-2 w-full mt-2">
                <Link to="/login">
                  <Button
                    className="w-full"
                    style={{ backgroundColor: '#C4A084', color: 'white' }}
                    data-testid="magic-login-back-to-login"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" /> Zurück zum Login
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MagicLogin;
